// =============================================
// useTransferDomains — lista domen-kandydatow dla kreatora "Przenies domeny"
// i akcji inline w "Domeny w CF".
//
// Gating Sponsorakow (przeniesione 1:1 z dawnego /dns):
//   * select workspace pokazuje Sponsoraki TYLKO z flaga can_access_sideprojects,
//   * effectiveWorkspace = twardy guard poza UI (bez flagi zawsze 'firma'),
//   * RLS na `websites` (can_see_workspace) i tak nie zwroci Sponsorakow bez flagi.
// IP hostingu: z useWebsiteHostingMap (hosting_ip_address); 'EMPTY' / zle IP
// pomijane; kilka hostingow z roznymi IP → ipAmbiguous + candidateIps.
// =============================================
import { useEffect, useMemo, useState } from "react";
import { useWebsites } from "@/hooks/useWebsites";
import { useWebsiteHostingMap } from "@/hooks/useWebsiteHostingMap";
import { useCanAccessSideprojects } from "@/hooks/useCanAccessSideprojects";
import { websiteDomainKey, type AmWorkspace } from "@/services/aftermarketService";
import { isValidIPv4 } from "@/services/cloudflareService";
import type { TransferDomain } from "@/services/cloudflare/transferTypes";

export interface TransferDomainsState {
  workspace: AmWorkspace;
  setWorkspace: (ws: AmWorkspace) => void;
  canSideprojects: boolean | null;
  effectiveWorkspace: AmWorkspace;
  /** domeny z WYBRANEGO workspace (kreator, krok 1) */
  domains: TransferDomain[];
  /** domeny ze wszystkich workspace'ow widocznych przez RLS (akcje inline w Domeny w CF) */
  allDomains: TransferDomain[];
  /** domena po kluczu (allDomains) — null gdy nie ma w panelu */
  findDomain: (key: string) => TransferDomain | null;
  loading: boolean;
}

export function useTransferDomains(): TransferDomainsState {
  const [workspace, setWorkspace] = useState<AmWorkspace>("firma");
  const { canAccess: canSideprojects } = useCanAccessSideprojects();
  useEffect(() => {
    if (canSideprojects === false && workspace === "sideprojects") setWorkspace("firma");
  }, [canSideprojects, workspace]);
  const effectiveWorkspace: AmWorkspace = canSideprojects === true ? workspace : "firma";

  const { websites, loading: websitesLoading } = useWebsites();
  const { mappings, loading: mapLoading } = useWebsiteHostingMap();

  const ipByWebsite = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const mp of mappings) {
      const ip = (mp.hosting_ip_address || "").trim();
      if (!ip || ip.toUpperCase() === "EMPTY" || !isValidIPv4(ip)) continue;
      const arr = m.get(mp.website_id) ?? [];
      if (!arr.includes(ip)) arr.push(ip);
      m.set(mp.website_id, arr);
    }
    return m;
  }, [mappings]);

  const allDomains = useMemo(() => {
    const seen = new Set<string>();
    const out: TransferDomain[] = [];
    for (const w of websites) {
      const key = websiteDomainKey(w);
      if (!key || seen.has(key)) continue; // duplikat → pierwszy wygrywa (jak w sync dat)
      seen.add(key);
      const exp = (w.expiry_date || "").trim();
      const ips = ipByWebsite.get(w.id) ?? [];
      const ws: AmWorkspace = w.workspace === "sideprojects" ? "sideprojects" : "firma";
      // Twardy guard: bez flagi wiersze sideprojects nie trafiaja nawet do allDomains
      // (RLS i tak ich nie zwroci — podwojne zabezpieczenie).
      if (ws === "sideprojects" && canSideprojects !== true) continue;
      out.push({
        key,
        websiteId: w.id,
        workspace: ws,
        expiry: exp && exp.toUpperCase() !== "EMPTY" ? exp : null,
        expirySource: w.expiry_source ?? null,
        hostingIp: ips.length === 1 ? ips[0] : null,
        ipAmbiguous: ips.length > 1 || undefined,
        candidateIps: ips.length > 1 ? ips : undefined,
      });
    }
    return out.sort((a, b) => a.key.localeCompare(b.key));
  }, [websites, ipByWebsite, canSideprojects]);

  const domains = useMemo(() => allDomains.filter((d) => d.workspace === effectiveWorkspace), [allDomains, effectiveWorkspace]);

  const byKey = useMemo(() => new Map(allDomains.map((d) => [d.key, d] as const)), [allDomains]);
  const findDomain = (key: string) => byKey.get(key) ?? null;

  return {
    workspace, setWorkspace, canSideprojects, effectiveWorkspace,
    domains, allDomains, findDomain,
    loading: websitesLoading || mapLoading,
  };
}
